
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CompanyPerks } from '@/lib/types';

export const useCompanyPerks = (companyId: string) => {
  const [perks, setPerks] = useState<CompanyPerks | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyPerks = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('company_perks')
          .select('*')
          .eq('company_id', companyId)
          .maybeSingle();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          const formattedPerks: CompanyPerks = {
            id: data.id,
            companyId: data.company_id,
            perk1: data.perk1 || undefined,
            perk2: data.perk2 || undefined,
            perk3: data.perk3 || undefined,
            perk4: data.perk4 || undefined,
            perk5: data.perk5 || undefined,
            createdAt: new Date(data.created_at).getTime(),
          };
          setPerks(formattedPerks);
        } else {
          setPerks(null);
        }
      } catch (err: any) {
        console.error('Error fetching company perks:', err);
        setError(err.message || 'Failed to load company perks');
        setPerks(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyPerks();
  }, [companyId]);

  return {
    perks,
    loading,
    error,
    hasPerks: !!perks && Object.values(perks).some(perk => 
      typeof perk === 'string' && perk.trim().length > 0
    )
  };
};
